import { COLOR } from 'app-v2/design-tokens'
import { Chevron, Marker } from 'app/assets/icons'
import { ResponsiveContainer, media, style } from 'app/assets/styles'
import { Link } from 'app/common/link'
import { Buttons, OpeningStatus } from 'app/common/restaurant'
import {
    CaptionText,
    H4,
    H5,
    SmallText,
    Text,
    fonts
} from 'app/common/typography'
import { CcCartCheck } from 'app/ecommerce/click-and-collect/cart-check'
import React from 'react'
import { Collapse } from 'react-collapse'
import type { IntlShape } from 'react-intl'
import { FormattedMessage, injectIntl } from 'react-intl'
import { colors as oldColors } from 'shared/assets/styles'
import { eventsDefinitions } from 'shared/store/metadata'
import type { Coordinates } from 'shared/store/store-locator'
import type { RestaurantShortDTO } from 'shared/types'
import { isEcommerceModeActive } from 'shared/utils/ecommerce'
import styled from 'styled-components'

const { yellow, grey30, grey } = oldColors

type Props = {
    restaurant: RestaurantShortDTO,
    handleToggle: () => void,
    collapsed: boolean,
    origin: ?Coordinates,
    intl: IntlShape,
    clickAndCollect?: boolean
}

const RestaurantCardMobileComponent = ({
    restaurant,
    handleToggle,
    origin,
    collapsed,
    intl,
    clickAndCollect
}: Props) => {
    const {
        status,
        cadStatus,
        name,
        id,
        addressFull,
        services,
        distance,
        pageUrl,
        external,
        lat,
        lng,
        deliverooUrl,
        uberEatsUrl,
        cacStatus,
        driveStatus,
        openingHour,
        closingHour
    } = restaurant
    let metaRestaurantSheet = { ...eventsDefinitions['RESTAURANT_SHEET'] }
    metaRestaurantSheet.event_label = name
    let metaRestaurantDirections = {
        ...eventsDefinitions['RESTAURANT_DIRECTIONS'],
        event_label: name
    }

    const renderClickAndCollect = () => {
        const disabled = !isEcommerceModeActive(restaurant)
        return (
            <CCContainer>
                <CCHeader>
                    <Marker width={28} height={38} />
                    <CCHeaderText>
                        <CCRestaurantName>{name}</CCRestaurantName>
                        <CCDistance>{distance}</CCDistance>
                    </CCHeaderText>
                    <CCChevron
                        color="black"
                        direction={collapsed ? 'down' : 'up'}
                    />
                </CCHeader>
                <CCInfos>
                    <CCAddress color={COLOR.PRIMARY}>{addressFull}</CCAddress>
                    <OpeningStatus
                        id={id}
                        status={status}
                        driveStatus={driveStatus}
                        openingHour={openingHour}
                        closingHour={closingHour}
                    />
                </CCInfos>
                <CCButton>
                    <CcCartCheck disabled={disabled} restaurant={restaurant}>
                        <SelectRestaurantButton disabled={disabled}>
                            <FormattedMessage id="clickandcollect.map.select.restaurant">
                                {(text: string) => (
                                    <H4 color="white">{text}</H4>
                                )}
                            </FormattedMessage>
                        </SelectRestaurantButton>
                    </CcCartCheck>
                </CCButton>
            </CCContainer>
        )
    }

    return (
        <Card>
            <Header onClick={handleToggle}>
                {clickAndCollect ? (
                    renderClickAndCollect()
                ) : (
                    <div>
                        <FlexRow>
                            <H5 font={fonts.roboto}>{name}</H5>
                            <Chevron
                                color={COLOR.PRIMARY}
                                direction={collapsed ? 'down' : 'up'}
                            />
                        </FlexRow>
                        <FlexRow>
                            <OpeningStatus
                                id={id}
                                status={status}
                                driveStatus={driveStatus}
                                openingHour={openingHour}
                                closingHour={closingHour}
                            />
                            <Text>{distance}</Text>
                        </FlexRow>
                        <Buttons
                            origin={origin}
                            destination={{ lat, lng }}
                            metaDirection={metaRestaurantDirections}
                            services={services}
                            deliverooUrl={deliverooUrl}
                            uberEatsUrl={uberEatsUrl}
                            cacStatus={cacStatus}
                            restaurant={restaurant}
                            className="restaurantDetail"
                        />
                    </div>
                )}
            </Header>
            {!clickAndCollect && (
                <Collapse isOpened={collapsed}>
                    <FormattedMessage id="storelocator.restaurant.button.restaurant">
                        {(text: string) => (
                            <StyledRestaurantLink
                                external={external}
                                url={`${intl.formatMessage({
                                    id: 'routes.restaurant'
                                })}/${pageUrl}`}
                                meta={metaRestaurantSheet}>
                                <StyledRestaurantLinkText>
                                    {text}
                                </StyledRestaurantLinkText>
                            </StyledRestaurantLink>
                        )}
                    </FormattedMessage>
                    <AddressContainer>
                        <Marker width={28} height={38} />
                        <StyledText color={grey}>{addressFull}</StyledText>
                    </AddressContainer>
                </Collapse>
            )}
        </Card>
    )
}

const StyledRestaurantLink = styled(Link)`
    padding: 12px 0;
`

const StyledRestaurantLinkText = styled(SmallText)`
    text-decoration: underline;
`

const StyledText = styled(Text)`
    margin-left: 20px;
`

const Card = styled(ResponsiveContainer)`
    padding-top: 20px;
    .ReactCollapse--collapse {
        transition: height 500ms;
    }
`

const FlexRow = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding-bottom: 5px;
`
const Header = styled.div`
    padding-bottom: 10px;
`

const AddressContainer = styled.div`
    display: flex;
    padding: 15px 0 10px;
    align-items: center;
`

const CCContainer = styled.div`
    padding: 0 10px 10px;
    ${media.medium`
        width: calc(100vw - 80px);
        max-width: 1280px;
    `}
`

const CCHeader = styled.div`
    display: flex;
    flex-direction: row;
    ${media.medium`
        margin-left: 50px;
    `}
`

const CCHeaderText = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-top: -10px;
`

const CCRestaurantName = styled(H4)`
    margin-left: 10px;
`

const CCDistance = styled(SmallText)`
    margin-left: 20px;
`

const CCChevron = styled(Chevron)`
    position: absolute;
    right: 10px;
    ${media.medium`
        right: 50px;
    `}
`

const CCInfos = styled.div`
    margin: -10px 0 10px 35px;
    ${media.medium`
        margin-left: 85px;
    `}
`

const CCButton = styled.div`
    display: flex;
    justify-content: center;
    ${media.medium`
        width: calc(100vw - 100px);
        max-width: 1260px;
    `}
`

const SelectRestaurantButton = styled.div`
    ${style.buttonMedium};
    align-self: center;
    border-radius: 10px;
    border: none;
    background: ${props => (props.disabled ? grey30 : yellow)};
`

const CCAddress = styled(CaptionText)``

export const RestaurantCardMobile = injectIntl(RestaurantCardMobileComponent)
